import { useApp } from 'contexts/app';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AccordionRubric, Avatar, Button, Stack, styled, Text, TextInput } from 'shell/components';
import { getLatestConfirmedId, getRubricMaxPoints, getUserPreferredFullName, isValidRate } from 'utils/helper';

const RubricRenderer = ({
    disabled = false,
    showGraded = true,
    showGradingExplanationInput,
    showSuggestedTag = true,
    assignment,
    currentSubmission,
    selectedFileId,
    setSelectedFileId,
    locations = [],
    form,
    setForm,
    gradeOverrides,
    setGradeOverrides,
    selectedCriterionId,
    setSelectedCriterionId,
    setShowFailedDialog,
    handleCriterionNavigation,
    hideCriterionNavigation = false,
}) => {
    const { accountUsers } = useApp();
    const { t } = useTranslation('assignment');

    const locationData = useMemo(() => {
        return {
            locations,
            files: currentSubmission?.files ?? [],
        };
    }, [locations, currentSubmission?.files]);

    const rubric = useMemo(
        () =>
            assignment.rubric.criteria.map((criterion) => {
                const justification = currentSubmission.ai?.grading?.[criterion.id]?.justification;
                const suggestedOptionId = currentSubmission.ai?.grading?.[criterion.id]?.optionId;
                const gradingInstructions =
                    assignment.ai_grading_settings?.criteria?.[criterion.id]?.grading_instructions;
                if (!suggestedOptionId) return { ...criterion, gradingInstructions };
                return {
                    ...criterion,
                    justification,
                    options: criterion.options.map((option) => ({
                        ...option,
                        suggested: showSuggestedTag ? option.id === suggestedOptionId : false,
                    })),
                    gradingInstructions,
                };
            }),
        [assignment, currentSubmission.ai?.grading, showSuggestedTag]
    );

    const isDirty = useMemo(() => {
        return Boolean(Object.keys(form?.criteria ?? {}).length) || Boolean(Object.keys(gradeOverrides).length);
    }, [form?.criteria, gradeOverrides]);

    const totalScore = useMemo(() => {
        if (!currentSubmission.ai?.grading && !isDirty) return '';
        if (!form) return '';
        if (Object.keys(gradeOverrides).includes('total')) return gradeOverrides.total;

        return rubric
            .reduce((acc, criterion) => {
                const doesOverrideExist = Object.keys(gradeOverrides).includes(criterion.id);
                if (doesOverrideExist && isValidRate(gradeOverrides[criterion.id])) {
                    return acc + Number(gradeOverrides[criterion.id]);
                }
                const selectedOptionId = form.criteria[criterion.id];
                if (selectedOptionId) {
                    return acc + Number(criterion.options.find(({ id }) => id === selectedOptionId).rate);
                }
                return acc + Number(criterion.options.find(({ suggested }) => suggested)?.rate || 0);
            }, 0)
            .toString();
    }, [currentSubmission?.ai?.grading, form, gradeOverrides, isDirty, rubric]);

    const maxTotalScore = useMemo(() => {
        return getRubricMaxPoints(rubric);
    }, [rubric]);

    const gradedByOwnerAccountId = getLatestConfirmedId(currentSubmission.sessions);
    const gradedByAccountUser = accountUsers.find(
        ({ owner_account_id }) => owner_account_id === gradedByOwnerAccountId
    );

    if (!form) return null;

    return (
        <Stack overflow={'auto'}>
            <BackgroundStack spacing={1}>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text variant={'p1'} weight={'semibold'}>
                        {t('rubric.title-rubric', 'Rubric')}
                    </Text>
                    <Stack alignItems={'center'} direction={'row'} spacing={1}>
                        <Text>{t('rubric.total-points', 'Total Points')}: </Text>
                        <OverrideTextInput
                            aria-label={'total points'}
                            disabled={disabled || currentSubmission.locked}
                            error={
                                !isDirty
                                    ? false
                                    : !isValidRate(totalScore) ||
                                      Number(totalScore) < 0 ||
                                      Number(totalScore) > maxTotalScore
                            }
                            isSuggestedScore={!Object.keys(gradeOverrides).length && !Object.keys(form.criteria).length}
                            size={'small'}
                            value={totalScore}
                            onInput={(e) => setGradeOverrides((current) => ({ ...current, total: e.target.value }))}
                        />
                        <Text>/ {maxTotalScore}</Text>
                    </Stack>
                </Stack>
                {gradedByAccountUser && (
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        <Text color={'light'} weight={'regular'}>
                            {t('graded-by', 'Graded by')}:
                        </Text>
                        <Avatar user={gradedByAccountUser} />
                        <Text>{getUserPreferredFullName(gradedByAccountUser)}</Text>
                    </Stack>
                )}
            </BackgroundStack>
            <Stack sx={{ p: 3 }} spacing={4}>
                {currentSubmission.is_failed && (
                    <Stack
                        sx={(theme) => ({
                            borderRadius: '8px',
                            p: theme.spacing(2, 3),
                            background: theme.palette.ensightful.warning[100],
                        })}
                        alignItems={'flex-start'}
                        spacing={2}
                    >
                        <Text color={(theme) => theme.palette.ensightful.warning[900]}>
                            {t(
                                'grading-section.unable-to-generate',
                                'We were unable to generate grades for this assignment. You can try to regenerate, or grade the submission manually. You must select grades and add feedback to confirm.'
                            )}
                        </Text>
                        <Button variant={'borderless'} onClick={() => setShowFailedDialog(true)}>
                            {t('common:ui.learn-more', 'Learn more')}
                        </Button>
                    </Stack>
                )}
                <AccordionRubric
                    showGraded={showGraded}
                    showGradingExplanationInput={
                        showGradingExplanationInput ??
                        ['custom', 'rubric', 'system'].includes(currentSubmission.ai?.feedback?.feedback_type)
                    }
                    rubric={rubric}
                    disabled={disabled || currentSubmission.locked}
                    form={form}
                    setForm={setForm}
                    gradeOverrides={gradeOverrides}
                    setGradeOverrides={setGradeOverrides}
                    selectedCriterionId={selectedCriterionId}
                    setSelectedCriterionId={setSelectedCriterionId}
                    handleCriterionNavigation={handleCriterionNavigation}
                    hideCriterionNavigation={hideCriterionNavigation}
                    locationData={locationData}
                    selectedFileId={selectedFileId}
                    setSelectedFileId={setSelectedFileId}
                />
            </Stack>
        </Stack>
    );
};

const BackgroundStack = styled(Stack)(({ theme }) => {
    return {
        width: '100%',
        padding: theme.spacing(2, 5),
        background: theme.palette.ensightful.primary[50],
        position: 'sticky',
        top: 0,
        zIndex: 1,
    };
});

const OverrideTextInput = styled(TextInput)(({ theme, isSuggestedScore }) => {
    return {
        width: '55px',
        '& > .Mui-disabled > input': {
            color: theme.palette.ensightful.neutral[900],
            '-webkitTextFillColor': 'unset',
        },
        ...(isSuggestedScore && {
            '& input.MuiInputBase-input': {
                color: theme.palette.ensightful.primary[500],
            },
        }),
    };
});

export default RubricRenderer;
